import { ReactNode, useState } from "react";
import { ChevronRight } from "lucide-react";
import Header from "@/shared/components/Header";
import CodeBlock from "@/shared/components/CodeBlock";
import { cn } from "@/shared/lib/utils";
import { curl, DocEndpoint, DocParam, resources } from "./resources";

const guideLinks = [
  { id: "introduction", title: "Introduction" },
  { id: "quickstart", title: "Quickstart" },
  { id: "versioning", title: "Versioning" },
  { id: "authentication", title: "Authentication" },
  { id: "scopes", title: "Scopes" },
  { id: "users-access", title: "Users & Access" },
  { id: "errors", title: "Errors" },
  { id: "supported-languages", title: "Supported Languages" },
  { id: "pagination", title: "Pagination" },
  { id: "date-time", title: "Date & Time" },
  { id: "amounts", title: "Amounts" },
  { id: "events", title: "Events" },
  { id: "file-imports", title: "File Imports" },
  { id: "static-ips", title: "Static IPs" },
  { id: "support", title: "Support" },
];

const authHeaders: DocParam[] = [
  {
    name: "User-Id",
    type: "string",
    required: true,
    description:
      "Identifier of the caller. For server-to-server access this is the integration id returned when the integration was created.",
  },
  {
    name: "User-Type",
    type: "string",
    required: true,
    description: "Type of the caller. API integrations use `integration`.",
  },
  {
    name: "Access-Time",
    type: "string",
    required: true,
    description:
      "Unix timestamp of the request, as a string. Must be within 300 seconds of server time.",
  },
  {
    name: "Access-Signature",
    type: "string",
    required: true,
    description: "Base64 ECDSA signature of the request message.",
  },
  {
    name: "Scope-Type",
    type: "string",
    description: "`holder` or `account`, when the route operates on a scope.",
  },
  {
    name: "Scope-Id",
    type: "string",
    description: "Identifier of the holder or account named by Scope-Type.",
  },
  {
    name: "Accept-Language",
    type: "string",
    description: "`english` or `portuguese`. Defaults to `english`.",
  },
];

const errorCodes = [
  ["invalid-structure", "The body or query is missing a parameter or carries an unknown one."],
  ["invalid-value", "A parameter is present but its value is not acceptable."],
  ["invalid-type", "A parameter has the wrong type."],
  ["not-found", "The referenced resource does not exist or is not visible in your scope."],
  ["already-exists", "The resource conflicts with one that already exists."],
  ["route-not-found", "The path or method does not exist."],
  ["cross-origin-unsupported", "The request is a browser preflight (`OPTIONS`). The API does not support cross-origin browser calls; call it from your backend."],
  ["blocked-scope", "The request carries no scope, or one that is not active, for an action that requires an active scope."],
  ["invalid-permission", "Your roles do not include the permission this route requires."],
  ["invalid-credentials", "The signature, timestamp, or credential did not verify."],
  ["rate-limit", "Too many requests. Retry later."],
];

function renderInline(text: string): ReactNode {
  const parts = text.split("`");
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <code
        key={`${part}-${index}`}
        className="px-1.5 py-0.5 rounded bg-card border border-border/50 text-foreground text-[0.85em]"
      >
        {part}
      </code>
    ) : (
      part
    )
  );
}

const httpMethods = ["GET", "POST", "PATCH"] as const;

type HttpMethod = (typeof httpMethods)[number];

function MethodBadge({ method }: { method: HttpMethod }) {
  return (
    <span
      className={cn(
        "inline-block px-2 py-0.5 rounded text-xs font-semibold tracking-wide",
        method === "GET" && "bg-primary/10 text-primary",
        method === "POST" && "bg-[#A3BE8C]/10 text-[#A3BE8C]",
        method === "PATCH" && "bg-[#D08770]/10 text-[#D08770]"
      )}
    >
      {method}
    </span>
  );
}

function isHttpMethod(value: string): value is HttpMethod {
  return (httpMethods as readonly string[]).includes(value);
}

function ParamsTable({ title, params }: { title: string; params: DocParam[] }) {
  return (
    <div className="mt-6">
      <h4 className="text-sm font-semibold text-foreground mb-3">{title}</h4>
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {params.map((param) => (
                <tr key={`${param.type} ${param.name}`} className="border-b border-border/50 last:border-b-0">
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    <code className="text-foreground font-medium">{param.name}</code>
                    {isHttpMethod(param.type) ? (
                      <div className="mt-1.5">
                        <MethodBadge method={param.type} />
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground/60 mt-1">
                        {param.type}
                        {param.required ? " · required" : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {renderInline(param.description)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EndpointBlock({ endpoint }: { endpoint: DocEndpoint }) {
  return (
    <div id={endpoint.id} className="mt-10 first:mt-8 scroll-mt-24">
      {endpoint.legacyId && (
        <span id={endpoint.legacyId} className="block h-0 scroll-mt-24" aria-hidden="true" />
      )}
      <h3 className="text-lg font-semibold text-foreground">{endpoint.summary}</h3>
      <div className="mt-2 flex items-center gap-2">
        <MethodBadge method={endpoint.method} />
        <code className="text-sm text-muted-foreground">{endpoint.path}</code>
      </div>
      {endpoint.description.map((paragraph) => (
        <p key={paragraph} className="mt-3 text-muted-foreground leading-relaxed">
          {renderInline(paragraph)}
        </p>
      ))}
      {endpoint.queryParams && (
        <ParamsTable title="Query parameters" params={endpoint.queryParams} />
      )}
      {endpoint.bodyParams && (
        <ParamsTable title="Body parameters" params={endpoint.bodyParams} />
      )}
      {endpoint.responseParams && (
        <ParamsTable title="Response fields" params={endpoint.responseParams} />
      )}
      {endpoint.requestExample && (
        <CodeBlock code={endpoint.requestExample} title="Request" className="mt-6" />
      )}
      {endpoint.responseExample && (
        <CodeBlock code={endpoint.responseExample} title="Response" className="mt-4" />
      )}
    </div>
  );
}

function DocSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 pt-14 border-t border-border/50 first:border-t-0 first:pt-0">
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{title}</h2>
      {children}
    </section>
  );
}

function Paragraph({ children }: { children: ReactNode }) {
  return (
    <p className="mt-4 text-muted-foreground leading-relaxed">
      {typeof children === "string" ? renderInline(children) : children}
    </p>
  );
}

function SubHeading({ children }: { children: ReactNode }) {
  return <h3 className="mt-10 text-xl font-semibold text-foreground">{children}</h3>;
}

interface SidebarLink {
  id: string;
  title: string;
  subitems?: { id: string; title: string }[];
}

function SidebarGroup({ title, links }: { title: string; links: SidebarLink[] }) {
  const [openById, setOpenById] = useState<Record<string, boolean>>({});

  const toggle = (id: string) =>
    setOpenById((previous) => ({ ...previous, [id]: !previous[id] }));

  const expand = (id: string) =>
    setOpenById((previous) => ({ ...previous, [id]: true }));

  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60 mb-3">
        {title}
      </div>
      <ul className="space-y-2">
        {links.map((link) => (
          <li key={link.id}>
            <div className="flex items-center justify-between gap-2">
              <a
                href={`#${link.id}`}
                onClick={() => link.subitems && expand(link.id)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.title}
              </a>
              {link.subitems && (
                <button
                  type="button"
                  onClick={() => toggle(link.id)}
                  aria-expanded={!!openById[link.id]}
                  aria-label={`${openById[link.id] ? "Collapse" : "Expand"} ${link.title} endpoints`}
                  className="text-muted-foreground/60 hover:text-foreground transition-colors"
                >
                  <ChevronRight
                    className={cn(
                      "h-3.5 w-3.5 transition-transform",
                      openById[link.id] && "rotate-90"
                    )}
                  />
                </button>
              )}
            </div>
            {link.subitems && openById[link.id] && (
              <ul className="mt-1.5 mb-2 ml-3 space-y-1.5 border-l border-border/50 pl-3">
                {link.subitems.map((subitem) => (
                  <li key={subitem.id}>
                    <a
                      href={`#${subitem.id}`}
                      className="text-xs text-muted-foreground/70 hover:text-foreground transition-colors"
                    >
                      {subitem.title}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function DocsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <div className="container max-w-6xl mx-auto px-4 md:px-6 pt-28 pb-24 flex gap-12">
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-28 max-h-[calc(100vh_-_9rem)] overflow-y-auto overscroll-contain pr-3 space-y-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <SidebarGroup title="Getting started" links={guideLinks} />
            <SidebarGroup
              title="API resources"
              links={resources.map((resource) => ({
                id: resource.id,
                title: resource.title,
                subitems: resource.endpoints.map((endpoint) => ({
                  id: endpoint.id,
                  title: endpoint.summary,
                })),
              }))}
            />
          </div>
        </aside>

        <main className="flex-1 min-w-0 space-y-14">
          <div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight">
              API Documentation
            </h1>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Everything you need to integrate with the Veltiz API: authentication,
              conventions, and the resource reference.
            </p>
          </div>

          <DocSection id="introduction" title="Introduction">
            <Paragraph>
              The Veltiz API is a REST API. Requests and responses are JSON, and every
              request is made over HTTPS against an environment host:
            </Paragraph>
            <ParamsTable
              title="Environments"
              params={[
                {
                  name: "Sandbox",
                  type: "https://sandbox.api.veltiz.com",
                  description:
                    "Build and test your integration against isolated data.",
                },
                {
                  name: "Production",
                  type: "https://api.veltiz.com",
                  description: "The live environment.",
                },
              ]}
            />
            <Paragraph>
              Creation endpoints always receive and return an array of objects, even for
              a single item, so batch sizes can grow without breaking your integration.
              Response keys use the last word of the resource name: an AccountHolder
              arrives under `holders`, an ExternalTrade under `trades`.
            </Paragraph>
            <Paragraph>
              Quantities and prices are never floating-point numbers. Each value is an
              integer amount paired with a scale giving its decimal places: R$ 38.41 is
              amount 3841 with scale 2.
            </Paragraph>
          </DocSection>

          <DocSection id="quickstart" title="Quickstart">
            <Paragraph>
              A first integration, end to end. Each step names the section that
              details it:
            </Paragraph>
            <ol className="mt-4 space-y-3 text-muted-foreground leading-relaxed list-decimal pl-6">
              {[
                "Create your credential. Generate a key pair and have an administrator create an Integration in the Console with the roles your system needs — see Authentication. Keep the integration id: it is your `User-Id`.",
                "Find your scope. `GET /account-holder` returns your organization; `GET /account` under holder scope lists its accounts. Most of what follows runs with the account's `Scope-Type: account` headers.",
                "Resolve instruments. Look your instruments up in the catalog (`GET /equity-instrument`, `/debt-instrument`, `/currency-instrument`) and keep their ids. Anything missing: search with `POST /instrument-request/preview`, request it with `POST /instrument-request`, and poll the request until it reaches `success`.",
                "Create investors and portfolios. `POST /investor` registers the owners; each may carry its first portfolio in the same call, read back with `GET /portfolio?owner_id=...`.",
                "Load the holdings. `POST /external-trade` books each off-platform execution against a portfolio. Send a `unique_id` per trade so retries cannot double-book. To load them from a file instead, upload a CSV with `POST /attachment`, wait for its scan to reach `success`, then ask for the parse: `POST /position-parse-request` for a statement of holdings, or `POST /trade-parse-request` for a blotter of executions, each naming the attachment and the target portfolio — see File Imports.",
                "Read the result. `GET /position` shows the derived holdings marked at current prices; `GET /position/summary` consolidates a portfolio into NAV, weights, and allocations.",
                "Keep following. Poll list endpoints by `status` and use the `GET /{resource}/event` histories to audit state changes — see Events. Positions are the exception: they carry no status filter and no event route, so read them fresh from `GET /position`.",
              ].map((step) => (
                <li key={step}>{renderInline(step)}</li>
              ))}
            </ol>
          </DocSection>

          <DocSection id="versioning" title="Versioning">
            <Paragraph>
              The API version is the first segment of every path, as in
              `/v0/account-holder`. Breaking changes ship as a new version segment;
              existing versions keep working until they are formally deprecated.
              Requests to an unknown version are rejected with a validation error.
            </Paragraph>
            <Paragraph>
              The version only increases when compatibility breaks. The following
              changes are considered backward-compatible and can happen within a
              version, so your integration must tolerate them:
            </Paragraph>
            <ul className="mt-4 space-y-2 text-muted-foreground leading-relaxed list-disc pl-6">
              {[
                "Adding new endpoints and new resources.",
                "Adding new optional request parameters, in the query or the body.",
                "Adding new keys to response objects.",
                "Adding new values to enumerated fields, such as a new status, subtype, or error code.",
                "Changing the order of keys inside JSON objects.",
                "Changing the text of human-readable strings, such as error messages and display names. Only the error `code` is contract.",
                "Changing the length or format of opaque values, such as ids and pagination cursors. Store and compare them; never parse them.",
              ].map((item) => (
                <li key={item}>{renderInline(item)}</li>
              ))}
            </ul>
          </DocSection>

          <DocSection id="authentication" title="Authentication">
            <Paragraph>
              The Veltiz API uses ECDSA digital signatures instead of API keys. You
              hold a private key and sign every request with it; Veltiz holds only the
              matching public key and verifies each signature. No secret ever travels
              with a request, so there is nothing in transit to intercept and replay
              beyond the request itself, and the timestamp check bounds even that.
            </Paragraph>
            <Paragraph>
              Keys are secp256k1 ECDSA key pairs with SHA-256 as the digest — the same
              scheme used across the platform.
            </Paragraph>

            <SubHeading>Create your key pair</SubHeading>
            <Paragraph>
              Generate the pair yourself, on the machine that will use it. Veltiz never
              generates, receives, or stores your private key — only the public half is
              ever registered.
            </Paragraph>
            <CodeBlock
              className="mt-6"
              title="Generate a secp256k1 key pair"
              code={`openssl ecparam -name secp256k1 -genkey -noout -out private-key.pem
openssl ec -in private-key.pem -pubout -out public-key.pem`}
            />

            <SubHeading>Create an Integration</SubHeading>
            <Paragraph>
              An Integration is the server-to-server identity your systems call the API
              with. It binds your public key to a set of roles inside a scope — the
              holder or account the integration may act in.
            </Paragraph>
            <Paragraph>
              Integrations are created in the Console. Sign in, select the organization
              or account the integration will act in, and create it with a name that
              identifies your system, the public key in PEM format, the roles granting
              the permissions it needs, and optionally the IP addresses allowed to use
              it.
            </Paragraph>
            <Paragraph>
              The Console shows the new integration's id — that id is the `User-Id`
              your requests will carry. The integration becomes active moments after
              creation, and from then on it can call every route its roles permit.
            </Paragraph>

            <SubHeading>Sign your requests</SubHeading>
            <Paragraph>
              Every request carries a signature over a message built from three parts,
              joined by colons: the `User-Id` header value, the `Access-Time` header
              value, and the request body — the exact JSON string you send, byte for
              byte. A request without a body uses an empty string, so its message ends
              with a colon:
            </Paragraph>
            <CodeBlock
              className="mt-6"
              title="Signature message"
              code={`message = "{User-Id}:{Access-Time}:{body}"

POST  9a426f1d8c3b5a7e4f9c2d0b8a6e4c2f0d8b:1786190539:{"requests": [{"figi": "BBG000B9XRY4"}]}
GET   9a426f1d8c3b5a7e4f9c2d0b8a6e4c2f0d8b:1786190539:`}
            />
            <Paragraph>
              Sign the message with your private key — ECDSA over secp256k1 with
              SHA-256 — and send the signature, Base64-encoded, in the
              `Access-Signature` header. The body you sign and the body you send must
              be the same string: serialize once and reuse it, because any difference,
              even whitespace, invalidates the signature.
            </Paragraph>
            <Paragraph>
              This applies to file uploads too: a multipart body is signed as the
              exact bytes you send, boundaries included. See File Imports.
            </Paragraph>
            <CodeBlock
              className="mt-6"
              title="Building and sending a signed request (pseudo-code)"
              code={`integration_id = "9a426f1d8c3b5a7e4f9c2d0b8a6e4c2f0d8b"
private_key    = secret_manager.read("veltiz-private-key")

body        = '{"requests": [{"figi": "BBG000B9XRY4"}]}'
access_time = string(unix_time_now())

message   = integration_id + ":" + access_time + ":" + body
signature = base64(ecdsa_sign(message, private_key, curve=secp256k1, hash=sha256))

response = http_post(
  url     = "https://sandbox.api.veltiz.com/v0/instrument-request",
  headers = {
    "Content-Type":     "application/json",
    "User-Id":          integration_id,
    "User-Type":        "integration",
    "Access-Time":      access_time,
    "Access-Signature": signature,
    "Scope-Type":       "holder",
    "Scope-Id":         holder_id,
  },
  body = body,
)`}
            />
            <ParamsTable title="Request headers" params={authHeaders} />
            <Paragraph>
              Routes that operate inside an organization or account take the scope from
              the `Scope-Type` and `Scope-Id` headers, never from the body. Calling a
              scoped route without them is rejected with a validation error naming the
              missing header.
            </Paragraph>
            <CodeBlock
              className="mt-6"
              title="Signed request"
              code={curl({ method: "GET", path: "/account", scopeType: "holder" })}
            />

            <SubHeading>Protect your private key</SubHeading>
            <Paragraph>
              Your private key is the credential. Anyone holding it can act as your
              integration, and no one — including Veltiz — ever needs it: any request
              for your private key is fraudulent.
            </Paragraph>
            <Paragraph>
              Never commit the key to source control, embed it in a client-side or
              mobile application, or send it over chat and email. Store it in a secrets
              manager or an HSM — Google Secret Manager, AWS Secrets Manager, or Vault —
              encrypted at rest and readable only by the runtime that signs requests.
              Give each system its own integration with the fewest roles that let it do
              its job, and restrict the allowed IP addresses where your egress is
              stable.
            </Paragraph>
            <Paragraph>
              To rotate a key, create a new integration with the new public key, move
              your traffic, and close the old integration — a closed integration stops
              being accepted immediately.
            </Paragraph>

            <SubHeading>Server-to-server only</SubHeading>
            <Paragraph>
              The API is built for server-to-server integration. It returns no CORS
              headers and refuses the browser preflight (`OPTIONS`) request with a 400
              `cross-origin-unsupported` error, so a call made directly from a web page
              on another origin fails. Call Veltiz from your backend, and let your own
              frontend talk to your backend.
            </Paragraph>
          </DocSection>

          <DocSection id="scopes" title="Scopes">
            <Paragraph>
              A scope is the context a request acts in. The API offers two scope
              types: `holder` — the whole organization, everything under one holder —
              and `account` — a single account inside it, such as one fund or one
              vehicle.
            </Paragraph>
            <Paragraph>
              Access is granted at a scope, never platform-wide: a user holds roles
              within an organization or within one of its accounts, and the grant
              reaches exactly that scope — a `holder` grant covers the organization
              and its accounts, an `account` grant that single account. Users, roles,
              and grants are covered in Users & Access.
            </Paragraph>
            <Paragraph>
              Requests that act inside an organization or account name the scope
              through the `Scope-Type` and `Scope-Id` headers: `Scope-Type` carries
              `holder` or `account`, and `Scope-Id` the id of that holder or account.
              Your permissions are resolved for that scope on every call, so the same
              credential can carry different roles in each.
            </Paragraph>
            <Paragraph>
              An `account` scope also identifies its holder, so endpoints that operate
              at the holder level — instrument requests, for example — accept either
              scope: under `account` scope, the account's holder is used. The reverse
              does not hold: a `holder` scope does not name an account, so endpoints
              that operate on account-level resources — investors, portfolios,
              positions, and external trades — require `account` scope. Endpoints that
              require a particular scope say so in their description. A request
              carrying no scope at all is stopped at the gateway with a
              `blocked-scope` error; one carrying an insufficient scope is rejected by
              the endpoint with a validation error.
            </Paragraph>
          </DocSection>

          <DocSection id="users-access" title="Users & Access">
            <Paragraph>
              A User is a credential, and users are global: one user can hold access
              to several organizations and accounts. What is scoped is the access,
              never the user — a user is granted access to an organization or an
              account, and that grant is what the `Scope-Type` and `Scope-Id` headers
              select on each request.
            </Paragraph>
            <Paragraph>
              Users are typed: a `person` is someone who signs in to the Console,
              identified by email and invited by an administrator; an `integration` is
              the server-to-server credential your systems call the API with; a
              `session` is the short-lived credential the Console creates for a
              signed-in person. Other types exist for Veltiz-managed identities.
            </Paragraph>
            <Paragraph>
              A grant binds a user to one scope with a set of roles, and roles carry
              permissions. Most endpoints require specific permissions, so what a
              credential can do inside a scope is what its roles allow — calling past
              them returns an `invalid-permission` error. The self-service routes
              below — `GET /role`, `GET /user/profile`, and `GET /user/access` — are
              deliberately open to any authenticated caller. Some route groups
              additionally require a product contracted by your organization, bound
              during onboarding: the portfolio-tracking routes in this reference are
              gated on the tracker product.
            </Paragraph>
            <Paragraph>
              The whole model is manageable through the API:
            </Paragraph>
            <ParamsTable
              title="Access endpoints"
              params={[
                {
                  name: "/role",
                  type: "GET",
                  description:
                    "Lists the roles assignable in the current scope — the source of the role ids used when creating integrations or granting access.",
                },
                {
                  name: "/user-access",
                  type: "GET",
                  description:
                    "Lists the grants made at the current scope — persons and integrations alike, each with their roles. Pages carry up to 10 entries.",
                },
                {
                  name: "/user-access",
                  type: "POST",
                  description:
                    "Grants a registered user access to the current scope, or replaces their existing grant with new roles.",
                },
                {
                  name: "/user-access",
                  type: "PATCH",
                  description: "Cancels a user's access to the current scope.",
                },
                {
                  name: "/user/profile",
                  type: "GET",
                  description: "Returns the calling user's own profile.",
                },
                {
                  name: "/user/access",
                  type: "GET",
                  description:
                    "Returns the calling user's own active grants, with the full roles — useful to see what your credential can do.",
                },
              ]}
            />
            <Paragraph>
              Each listing shows the grants made at the requested scope only. A
              `holder` grant reaches the holder's accounts, but it appears in the
              holder's listing, not in any account's — so auditing everything that can
              act on an account means reading the account scope and its holder scope,
              and the same goes for canceling. Canceling an access takes effect on the
              next request: grants are checked fresh on every authentication.
            </Paragraph>
          </DocSection>

          <DocSection id="errors" title="Errors">
            <Paragraph>
              The API uses exactly three HTTP status codes: 200 for success, 400 for
              any request that could not be accepted, and 500 for an unexpected
              failure on our side. The meaningful distinction lives in the error code,
              not the HTTP status.
            </Paragraph>
            <Paragraph>
              Whether to retry follows the error code, not the status. A 400 is
              deterministic for the same input — fix the request instead of replaying
              it — with one exception: `rate-limit` is a 400 whose request is fine, so
              back off and retry it. A 500 may be retried, and reads are always safe
              to retry.
            </Paragraph>
            <Paragraph>
              Retry writes deliberately. The duplicate guards make a retry
              non-duplicating, never completing: a retried trade `POST` carrying a
              `unique_id` that already landed is rejected with `already-exists` rather
              than booking twice — and since one duplicate rejects its whole batch,
              replaying a partially-landed batch books nothing. After an ambiguous
              failure, reconcile first — `GET /external-trade?unique_id=...` tells you
              which trades landed — then re-send only the missing ones.
            </Paragraph>
            <Paragraph>
              Failed requests return an `errors` array. Each error carries three
              fields: `code` identifies the rule that was violated, `message` is a
              human-readable explanation localized per `Accept-Language`, and
              `parameter` points at the offending input.
            </Paragraph>
            <CodeBlock
              className="mt-6"
              title="Error response"
              code={`{
  "errors": [
    {
      "code": "invalid-value",
      "message": "The value received is invalid.",
      "parameter": "body['trades'][0]['side']"
    }
  ]
}`}
            />
            <ParamsTable
              title="Common error codes"
              params={errorCodes.map(([code, description]) => ({
                name: code,
                type: "code",
                description,
              }))}
            />
          </DocSection>

          <DocSection id="supported-languages" title="Supported Languages">
            <Paragraph>
              Error messages and localized display strings are available in English and
              Portuguese. Pick the language per request with the `Accept-Language`
              header, set to `english` or `portuguese`. When the header is absent, the
              API answers in English. Resource field names, statuses, and error codes
              are language-independent.
            </Paragraph>
          </DocSection>

          <DocSection id="pagination" title="Pagination">
            <Paragraph>
              List endpoints paginate with an opaque cursor. Each page returns up to
              `limit` items — at most 100, which is also the default, though a few
              endpoints enforce a smaller cap stated in their parameter tables —
              together with a `cursor` value. Pass that `cursor` on the next call to
              fetch the following page. An empty `cursor` means there are no more
              pages.
            </Paragraph>
            <CodeBlock
              className="mt-6"
              title="Fetching the next page"
              code={`GET /v0/investor?limit=100
{"investors": [...], "cursor": "Q2zXhNc..."}

GET /v0/investor?limit=100&cursor=Q2zXhNc...
{"investors": [...], "cursor": ""}`}
            />
          </DocSection>

          <DocSection id="date-time" title="Date & Time">
            <Paragraph>
              Timestamps are ISO 8601 strings. Responses always carry UTC timestamps,
              such as `2026-08-11T12:22:19+00:00`. When sending a timestamp, the UTC
              offset is mandatory — `2026-08-11T09:22:19-03:00` is accepted,
              `2026-08-11T09:22:19` is not.
            </Paragraph>
            <Paragraph>
              Date-only values use `YYYY-MM-DD` and are interpreted in Brasília time
              (UTC-3). Filters such as `after` and `before` accept either a date or a
              full timestamp.
            </Paragraph>
          </DocSection>

          <DocSection id="amounts" title="Amounts">
            <Paragraph>
              Every quantity, price, and monetary value in the API is an integer. The
              API never sends or accepts floating-point numbers for these values,
              because binary floats cannot represent decimal fractions exactly — and
              rounding errors are not acceptable in financial data.
            </Paragraph>
            <Paragraph>
              Each value travels as a pair of fields: an integer `amount` carrying the
              digits, and an integer `scale` telling how many of those digits sit
              after the decimal point. The pair shares a prefix — `price_amount` goes
              with `price_scale`, `symbol_amount` with `symbol_scale`. The decimal
              value is `amount ÷ 10^scale`:
            </Paragraph>
            <CodeBlock
              className="mt-6"
              title="How amount and scale compose"
              code={`amount: 3841, scale: 2   ->  38.41
amount: 125,  scale: 1   ->  12.5
amount: 100,  scale: 0   ->  100
amount: 1,    scale: 8   ->  0.00000001`}
            />
            <Paragraph>
              A trade of 100 PETR4 shares at R$ 38.41 each therefore carries
              `symbol_amount` 100 with `symbol_scale` 0, and `price_amount` 3841 with
              `price_scale` 2. Scales are not fixed per field: each instrument
              declares its own `scale`, so read the scale from the payload every time
              instead of assuming one.
            </Paragraph>
            <CodeBlock
              className="mt-6"
              title="Converting to a decimal (pseudo-code)"
              code={`to_decimal(amount, scale):
    return decimal(amount) / decimal(10 ^ scale)

to_decimal(3841, 2)  ->  38.41

format(amount, scale):
    digits = string(amount)
    if scale == 0: return digits
    pad = left_pad(digits, scale + 1, "0")
    return insert(pad, length(pad) - scale, ".")

format(1, 8)  ->  "0.00000001"`}
            />
            <Paragraph>
              Convert to a decimal only at the edge of your system — to display a
              value or hand it to a report. For storage and arithmetic, keep the
              integer `amount` and `scale`: use a decimal type, never a binary float,
              and when two values must share a scale, rescale by multiplying the
              amount by `10^(new_scale - old_scale)` toward the larger scale — scaling
              down discards digits.
            </Paragraph>
            <Paragraph>
              Values meant only for display, such as `display_rate` on debt / fixed income
              instruments, arrive as ready-formatted strings instead of amount pairs.
              Render them as they come; they are not meant for arithmetic.
            </Paragraph>
          </DocSection>

          <DocSection id="events" title="Events">
            <Paragraph>
              Every resource on the platform is stored as an immutable sequence of
              events. A state change never updates the resource in place: it appends a
              new event carrying a complete snapshot of the resource as it stood at
              that moment.
            </Paragraph>
            <Paragraph>
              The most recent event is the current state — the object the resource's
              own list endpoint returns. Everything before it is the resource's
              history, and since nothing is ever rewritten, the sequence doubles as an
              audit trail.
            </Paragraph>
            <Paragraph>
              Each event carries its own `id`, the `type` of the transition (such as
              `created`, `activated`, or `failed`), the `reasons` behind a failure
              event (empty otherwise), the `created` timestamp of the snapshot, the
              `resource_type`, and the full snapshot under `resource`:
            </Paragraph>
            <CodeBlock
              className="mt-6"
              title="Event history of an investor"
              code={`{
  "events": [
    {
      "id": "cd0a9f2b6e4d8a1c4f7b2e5d8c0a3f6b9d1e",
      "type": "blocked",
      "reasons": [],
      "created": "2026-08-12T09:30:11+00:00",
      "resource_type": "investor",
      "resource": {
        "id": "5e9eb82f6d31a4c94e7f8b2d5a0c6e9f1b3d",
        "account_id": "7e1b72c4d9e18f3a4c5b8d6e2f0a9b1c3d5e",
        "name": "Ana Souza",
        "tax_id": "123.456.789-09",
        "status": "blocked",
        "tags": [],
        "created": "2026-08-11T12:10:02+00:00"
      }
    },
    {
      "id": "cd0a3e7c1a9f5b2d4e6a8c0f2b4d6e8a1c3f",
      "type": "created",
      "reasons": [],
      "created": "2026-08-11T12:10:02+00:00",
      "resource_type": "investor",
      "resource": {
        "id": "5e9eb82f6d31a4c94e7f8b2d5a0c6e9f1b3d",
        "account_id": "7e1b72c4d9e18f3a4c5b8d6e2f0a9b1c3d5e",
        "name": "Ana Souza",
        "tax_id": "123.456.789-09",
        "status": "active",
        "tags": [],
        "created": "2026-08-11T12:10:02+00:00"
      }
    }
  ],
  "cursor": ""
}`}
            />
            <Paragraph>
              {"Resources with a public history expose it at `GET /{resource}/event`, listed under each resource in this reference. Use it to audit who a resource has been over time — and when a resource ends up `failed`, read its latest event's `reasons` to learn why."}
            </Paragraph>
          </DocSection>

          <DocSection id="file-imports" title="File Imports">
            <Paragraph>
              Instead of posting each holding one at a time, a whole file can be
              imported: upload the file, then ask for it to be parsed into trades or
              into position snapshots.
            </Paragraph>
            <Paragraph>
              The file is stored as an Attachment, scanned, and then turned into rows
              by a Parse Request of one of two families: a Position Parse Request for
              a statement of holdings, a Trade Parse Request for a blotter of
              executions. Both resources are documented in this reference under
              Attachments and Parse Requests.
            </Paragraph>

            <SubHeading>The flow</SubHeading>
            <ol className="mt-4 space-y-3 text-muted-foreground leading-relaxed list-decimal pl-6">
              {[
                "Upload the file. `POST /attachment` stores it and starts a malware scan. The response carries the new attachment with status `created`.",
                "Wait for the scan. Poll `GET /attachment/{attachment_id}` until its status is `success`. A `rejected` attachment is quarantined and can never be parsed; a `failed` one never became retrievable. Retry with a new `unique_id` — the same key stays claimed for 30 days even after a failure.",
                "Ask for the parse. `POST /position-parse-request` reads a statement of holdings into position snapshots; `POST /trade-parse-request` reads a blotter of executions into external trades. Either names the attachment and the portfolio the rows belong to — the route fixes what the rows become, so the body carries no `type`. A holdings request also states `is_complete`: `true` declares the file the complete statement of the portfolio's holdings, so the platform closes every position the file does not list once every row is created. A holdings request may also carry `reference_time`, the instant that dates every row and the completeness declaration alike — a holdings file carries no date column of its own; a blotter request has neither field, because its rows must carry their own dates and it never declares completeness. One request produces rows for exactly one portfolio.",
                "Poll the request. `GET /position-parse-request/{request_id}` or `GET /trade-parse-request/{request_id}` moves through `created` and `processing` to one of four terminal statuses: `success`, `partial`, `rejected`, or `failed`.",
                "Read the rows. The request's result route — `GET /position-parse-request/{request_id}/result` or `GET /trade-parse-request/{request_id}/result` — returns the rows that created resources and the rows that were refused, each refused row carrying the reason it was refused. The route answers only once the request is terminal.",
                "Read the result. The created trades or snapshots are readable on their own routes, and `GET /position` shows the positions they produced.",
              ].map((step) => (
                <li key={step}>{renderInline(step)}</li>
              ))}
            </ol>

            <SubHeading>What you can upload today</SubHeading>
            <ul className="mt-4 space-y-2 text-muted-foreground leading-relaxed list-disc pl-6">
              {[
                "The service recognizes nine content types by reading the bytes, never the type the request declares: `application/pdf`, `image/png`, `image/jpeg`, `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`, `application/vnd.openxmlformats-officedocument.wordprocessingml.document`, `application/vnd.ms-excel`, `text/html`, `text/csv`, and `text/plain`.",
                "A public-API caller can send only the text formats today — `text/csv`, `text/html`, and `text/plain` — because the request signature is computed over the exact request body, and a binary body cannot be signed yet. The other six recognized formats have no route that can upload them today.",
                "A file is capped at 30 MiB.",
                "Parse requests read CSV only. The other text formats are stored but cannot yet be parsed.",
              ].map((item) => (
                <li key={item}>{renderInline(item)}</li>
              ))}
            </ul>

            <SubHeading>Signing a file upload</SubHeading>
            <Paragraph>
              {"The signature rule does not change for an upload: the message is still `{User-Id}:{Access-Time}:{body}`, and `body` is the exact request body, byte for byte. For a multipart upload the body is the whole multipart payload — every boundary line, header, and byte of file content included, exactly as it goes on the wire."}
            </Paragraph>
            <Paragraph>
              This means a tool that builds the multipart body for you, and picks its
              own boundary, cannot be used directly: you cannot sign bytes you have
              not composed. Build the body yourself, sign those exact bytes, and send
              them with a `Content-Type` naming the boundary you chose.
            </Paragraph>
            <CodeBlock
              className="mt-6"
              title="Signing a multipart upload (pseudo-code)"
              code={`boundary = "veltiz-" + random_hex(16)

data = '{"attachments": [{"unique_id": "xp-statement-2026-07"}]}'
file = read_bytes("statement.csv")

body = bytes(
  "--" + boundary + "\\r\\n" +
  'Content-Disposition: form-data; name="attachments"\\r\\n\\r\\n' +
  data + "\\r\\n" +
  "--" + boundary + "\\r\\n" +
  'Content-Disposition: form-data; name="file"; filename="statement.csv"\\r\\n' +
  "Content-Type: text/csv\\r\\n\\r\\n"
) + file + bytes("\\r\\n--" + boundary + "--\\r\\n")

access_time = string(unix_time_now())
message     = integration_id + ":" + access_time + ":" + body.decode("utf-8")
signature   = base64(ecdsa_sign(message, private_key, curve=secp256k1, hash=sha256))

response = http_post(
  url     = "https://sandbox.api.veltiz.com/v0/attachment",
  headers = {
    "Content-Type":     "multipart/form-data; boundary=" + boundary,
    "User-Id":          integration_id,
    "User-Type":        "integration",
    "Access-Time":      access_time,
    "Access-Signature": signature,
    "Scope-Type":       "account",
    "Scope-Id":         account_id,
  },
  body = body,
)`}
            />
            <Paragraph>
              To reproduce the same call with `curl`, write those exact bytes to a
              file named `upload-body.bin` and post them with
              `--data-binary @upload-body.bin`, with `Content-Type: multipart/form-data;
              boundary=$BOUNDARY` naming the same boundary you signed with — so the
              body curl sends is the body you signed. The Attachments upload
              endpoint's curl example is built exactly this way.
            </Paragraph>
          </DocSection>

          <DocSection id="static-ips" title="Static IPs">
            <Paragraph>
              Whenever Veltiz sends requests to your systems — webhook deliveries, for
              example — the traffic leaves our network from a fixed set of static IP
              addresses, one per environment and region. If your endpoints sit behind
              a firewall, allowlist these addresses to receive our calls.
            </Paragraph>
            <ParamsTable
              title="Egress IPs — South America (southamerica-east1)"
              params={[
                {
                  name: "Sandbox",
                  type: "34.95.198.246",
                  description: "All requests from the sandbox environment.",
                },
                {
                  name: "Production",
                  type: "to be published",
                  description:
                    "Published here before the production environment opens.",
                },
              ]}
            />
            <Paragraph>
              These addresses cover traffic from Veltiz to you. Your own calls to the
              API are unaffected — they go to the hosts in Introduction, whatever IP
              you call from. New regions add addresses to this list; additions are
              announced before they carry traffic.
            </Paragraph>
          </DocSection>

          <DocSection id="support" title="Support">
            <Paragraph>
              Questions, feedback, and problem reports are all welcome — write to{" "}
              <a
                href="mailto:tech@veltiz.com"
                className="text-primary hover:underline"
              >
                tech@veltiz.com
              </a>{" "}
              and the engineering team will answer. If something in this reference
              reads wrong or does not match what the API does, that is a bug in the
              documentation: please tell us.
            </Paragraph>
          </DocSection>

          {resources.map((resource) => (
            <DocSection key={resource.id} id={resource.id} title={resource.title}>
              {resource.description.map((paragraph) => (
                <Paragraph key={paragraph}>{paragraph}</Paragraph>
              ))}
              {resource.fields && (
                <ParamsTable title="Attributes" params={resource.fields} />
              )}
              {resource.endpoints.map((endpoint) => (
                <EndpointBlock
                  key={`${endpoint.method} ${endpoint.path} ${endpoint.summary}`}
                  endpoint={endpoint}
                />
              ))}
            </DocSection>
          ))}
        </main>
      </div>

      <footer className="py-10 px-4 md:px-6 bg-background border-t border-border">
        <div className="container max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <img
            src="https://storage.googleapis.com/veltiz-images/veltiz-logo-shadow2.svg"
            alt="Veltiz"
            className="h-2.5 md:h-3.5 w-auto mb-4 md:mb-0"
          />
          <div className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Veltiz. Revolutionizing the investment
            infrastructure.
          </div>
        </div>
      </footer>
    </div>
  );
}
